import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import {
  ArrowRightLeft,
  FilePlus2,
  FileSpreadsheet,
  Hourglass,
  LucideAngularModule,
  Receipt,
  ScrollText,
} from 'lucide-angular';
import { Chart } from 'chart.js/auto';
import { DashboardOverview, DashboardOverviewMonthlyFlowPoint } from '../../core/models/domain.models';
import { AuthService } from '../../core/services/auth.service';
import { DashboardService } from '../../core/services/dashboard.service';

type FlowGranularity = 'monthly' | 'quarterly' | 'yearly';
type AggregatedFlowPoint = {
  period: string;
  sale_mwh: number;
  purchase_mwh: number;
  banking_mwh: number;
};

@Component({
  standalone: true,
  selector: 'app-dashboard-page',
  imports: [CommonModule, RouterModule, LucideAngularModule],
  templateUrl: './dashboard.page.html',
  styleUrl: './dashboard.page.scss',
})
export class DashboardPage implements OnInit, AfterViewInit, OnDestroy {
  private readonly dashboard = inject(DashboardService);
  private readonly auth = inject(AuthService);

  protected readonly overview = signal<DashboardOverview | null>(null);
  protected readonly loading = signal(true);
  protected readonly flowGranularity = signal<FlowGranularity>('monthly');

  @ViewChild('monthlyFlowCanvas') private monthlyFlowCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('transactionCanvas') private transactionCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('scheduledCanvas') private scheduledCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('contractHealthCanvas') private contractHealthCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('contractTypeCanvas') private contractTypeCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('billingTypeCanvas') private billingTypeCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('paymentTrendCanvas') private paymentTrendCanvas?: ElementRef<HTMLCanvasElement>;

  private charts: Chart[] = [];
  private renderChartsFrame: number | null = null;
  private viewReady = false;

  protected readonly ScrollTextIcon = ScrollText;
  protected readonly FileSpreadsheetIcon = FileSpreadsheet;
  protected readonly FilePlus2Icon = FilePlus2;
  protected readonly ReceiptIcon = Receipt;
  protected readonly ArrowRightLeftIcon = ArrowRightLeft;
  protected readonly HourglassIcon = Hourglass;

  protected readonly quickActions = computed(() => {
    const actions = [
      { label: 'Add LOA', subtitle: 'Create new LOA', route: '/loas/new', icon: ScrollText },
      { label: 'Create Contract', subtitle: 'Start agreement draft', route: '/contracts/new', icon: FileSpreadsheet },
      { label: 'Punch Application', subtitle: 'Open application form', route: '/applications/new', icon: FilePlus2 },
      { label: 'Generate Bill', subtitle: 'Open OA billing', route: '/oa-billing', icon: Receipt },
    ];
    return this.auth.user()?.role === 'vendor'
      ? actions.filter((action) => action.route !== '/loas/new' && action.route !== '/contracts/new')
      : actions;
  });

  ngOnInit(): void {
    this.flowGranularity.set('monthly');
    this.dashboard.getOverview().subscribe({
      next: (overview) => {
        this.flowGranularity.set('monthly');
        this.overview.set(overview);
        this.loading.set(false);
        this.renderCharts();
      },
      error: () => {
        this.overview.set(null);
        this.loading.set(false);
      },
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.renderCharts();
  }

  ngOnDestroy(): void {
    if (this.renderChartsFrame !== null) {
      cancelAnimationFrame(this.renderChartsFrame);
      this.renderChartsFrame = null;
    }
    this.destroyCharts();
  }

  protected formatCompact(value: number): string {
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value || 0);
  }

  protected formatMwh(value: number): string {
    return `${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0)} MWh`;
  }

  protected formatCrore(value: number): string {
    return `₹ ${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0)} Cr`;
  }

  protected onFlowGranularityChange(value: string): void {
    const next = value === 'quarterly' || value === 'yearly' ? value : 'monthly';
    this.flowGranularity.set(next);
    this.renderCharts();
  }

  protected paymentStatusPercent(value: number, overview: DashboardOverview | null): number {
    if (!overview) return 0;
    const total =
      (overview.payment_status.paid_amount_cr || 0) +
      (overview.payment_status.due_amount_cr || 0) +
      (overview.payment_status.overdue_amount_cr || 0);
    if (!total) return 0;
    return Math.max(4, Math.round((value / total) * 100));
  }

  protected contractTypePercent(value: number, total: number): number {
    if (!total) return 0;
    return Math.round((value / total) * 100);
  }

  protected contractTypeTotal(distribution: Record<string, number>): number {
    return Object.values(distribution || {}).reduce((sum, value) => sum + (value || 0), 0);
  }

  protected recentTransactionStatusClass(status: string): string {
    return (status || '').toLowerCase() === 'approved' ? 'approved' : 'pending';
  }

  protected alertToneClass(tone: string): string {
    const normalized = (tone || '').toLowerCase();
    if (normalized === 'warning' || normalized === 'danger' || normalized === 'success') return normalized;
    return 'info';
  }

  private renderCharts(): void {
    if (!this.viewReady || !this.overview()) return;

    if (this.renderChartsFrame !== null) {
      cancelAnimationFrame(this.renderChartsFrame);
    }

    this.renderChartsFrame = requestAnimationFrame(() => {
      this.renderChartsFrame = requestAnimationFrame(() => {
        this.destroyCharts();
        this.renderMonthlyFlowChart();
        this.renderTransactionChart();
        this.renderScheduledChart();
        this.renderContractHealthChart();
        this.renderContractTypeChart();
        this.renderBillingTypeChart();
        this.renderPaymentTrendChart();
        this.charts.forEach((chart) => chart.resize());
        this.renderChartsFrame = null;
      });
    });
  }

  private renderMonthlyFlowChart(): void {
    const canvas = this.monthlyFlowCanvas?.nativeElement;
    const overview = this.overview();
    if (!canvas || !overview) return;
    const points = this.aggregateMonthlyFlow(overview.monthly_power_flow, this.flowGranularity());

    this.charts.push(
      new Chart(canvas, {
        type: 'bar',
        data: {
          labels: points.map((item) => item.period),
          datasets: [
            {
              label: 'Sale (MWh)',
              data: points.map((item) => item.sale_mwh),
              backgroundColor: '#114eb6',
              borderRadius: 8,
            },
            {
              label: 'Purchase (MWh)',
              data: points.map((item) => item.purchase_mwh),
              backgroundColor: '#3d82f6',
              borderRadius: 8,
            },
            {
              label: 'Banking (MWh)',
              data: points.map((item) => item.banking_mwh),
              backgroundColor: '#8cb7ff',
              borderRadius: 8,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          plugins: {
            legend: { position: 'bottom' },
            tooltip: {
              callbacks: {
                label: (context) => `${context.dataset.label}: ${this.formatTooltipMwh(Number(context.parsed.y) || 0)}`,
              },
            },
          },
          scales: {
            x: { grid: { display: false } },
            y: {
              beginAtZero: true,
              title: { display: true, text: 'Energy (MWh)', color: '#56708f', font: { weight: 700 } },
              ticks: { callback: (value) => this.formatAxisTick(Number(value) || 0) },
            },
          },
        },
      }),
    );
  }

  private aggregateMonthlyFlow(points: DashboardOverviewMonthlyFlowPoint[], granularity: FlowGranularity): AggregatedFlowPoint[] {
    if (!points.length) return [];
    if (granularity === 'monthly') {
      return points.map((item) => ({
        period: item.period,
        sale_mwh: item.sale_mwh,
        purchase_mwh: item.purchase_mwh,
        banking_mwh: item.banking_mwh,
      }));
    }
    if (granularity === 'yearly') {
      return [
        {
          period: 'Year',
          sale_mwh: this.sumFlow(points, 'sale_mwh'),
          purchase_mwh: this.sumFlow(points, 'purchase_mwh'),
          banking_mwh: this.sumFlow(points, 'banking_mwh'),
        },
      ];
    }

    const grouped: AggregatedFlowPoint[] = [];
    for (let index = 0; index < points.length; index += 3) {
      const bucket = points.slice(index, index + 3);
      grouped.push({
        period: bucket.length === 1 ? bucket[0].period : `${bucket[0].period}-${bucket[bucket.length - 1].period}`,
        sale_mwh: this.sumFlow(bucket, 'sale_mwh'),
        purchase_mwh: this.sumFlow(bucket, 'purchase_mwh'),
        banking_mwh: this.sumFlow(bucket, 'banking_mwh'),
      });
    }
    return grouped;
  }

  private sumFlow(points: DashboardOverviewMonthlyFlowPoint[], key: 'sale_mwh' | 'purchase_mwh' | 'banking_mwh'): number {
    return Number(points.reduce((sum, item) => sum + (item[key] || 0), 0).toFixed(2));
  }

  private formatAxisTick(value: number): string {
    if (value >= 1000) return `${Math.round(value / 1000)}k`;
    return `${Math.round(value)}`;
  }

  private formatTooltipMwh(value: number): string {
    return `${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value)} MWh`;
  }

  private renderTransactionChart(): void {
    const canvas = this.transactionCanvas?.nativeElement;
    const overview = this.overview();
    if (!canvas || !overview) return;

    this.charts.push(
      new Chart(canvas, {
        type: 'pie',
        data: {
          labels: Object.keys(overview.transaction_type_distribution),
          datasets: [
            {
              data: Object.values(overview.transaction_type_distribution),
              backgroundColor: ['#0f4eb7', '#1f6de2', '#75a8ff'],
              borderWidth: 1,
              borderColor: '#ffffff',
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          plugins: { legend: { position: 'bottom' } },
        },
      }),
    );
  }

  private renderScheduledChart(): void {
    const canvas = this.scheduledCanvas?.nativeElement;
    const overview = this.overview();
    if (!canvas || !overview) return;

    this.charts.push(
      new Chart(canvas, {
        type: 'line',
        data: {
          labels: overview.scheduled_vs_actual.map((item) => item.label),
          datasets: [
            {
              label: 'Scheduled',
              data: overview.scheduled_vs_actual.map((item) => item.scheduled_mwh),
              borderColor: '#7e8fa7',
              backgroundColor: '#7e8fa7',
              tension: 0.35,
            },
            {
              label: 'Actual',
              data: overview.scheduled_vs_actual.map((item) => item.actual_mwh),
              borderColor: '#2f6ff2',
              backgroundColor: '#2f6ff2',
              tension: 0.35,
            },
            {
              label: 'Deviation %',
              data: overview.scheduled_vs_actual.map((item) => item.deviation_percent),
              borderColor: '#f58b2d',
              backgroundColor: '#f58b2d',
              yAxisID: 'y1',
              tension: 0.35,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          plugins: { legend: { position: 'bottom' } },
          scales: {
            x: { grid: { display: false } },
            y: { beginAtZero: true },
            y1: {
              beginAtZero: true,
              position: 'right',
              grid: { drawOnChartArea: false },
            },
          },
        },
      }),
    );
  }

  private renderContractHealthChart(): void {
    const canvas = this.contractHealthCanvas?.nativeElement;
    const overview = this.overview();
    if (!canvas || !overview) return;

    this.charts.push(
      new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels: Object.keys(overview.contract_health),
          datasets: [
            {
              data: Object.values(overview.contract_health),
              backgroundColor: ['#26c281', '#4d7df3', '#ff6b6b', '#f7a41d'],
              borderWidth: 0,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          cutout: '62%',
          plugins: { legend: { position: 'right' } },
        },
      }),
    );
  }

  private renderContractTypeChart(): void {
    const canvas = this.contractTypeCanvas?.nativeElement;
    const overview = this.overview();
    if (!canvas || !overview) return;

    this.charts.push(
      new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels: Object.keys(overview.contract_type_distribution),
          datasets: [
            {
              data: Object.values(overview.contract_type_distribution),
              backgroundColor: ['#4d74ef', '#8f63f6', '#31b6a7'],
              borderWidth: 0,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          cutout: '70%',
          plugins: { legend: { position: 'bottom' } },
        },
      }),
    );
  }

  private renderBillingTypeChart(): void {
    const canvas = this.billingTypeCanvas?.nativeElement;
    const overview = this.overview();
    if (!canvas || !overview) return;

    this.charts.push(
      new Chart(canvas, {
        type: 'bar',
        data: {
          labels: overview.billing_by_type.map((item) => item.period),
          datasets: [
            {
              label: 'TMR',
              data: overview.billing_by_type.map((item) => item.tmr_amount_cr),
              backgroundColor: '#8c4bd7',
              stack: 'billing',
              borderRadius: 6,
            },
            {
              label: 'Surcharge',
              data: overview.billing_by_type.map((item) => item.surcharge_amount_cr),
              backgroundColor: '#f39a4a',
              stack: 'billing',
              borderRadius: 6,
            },
            {
              label: 'SOP',
              data: overview.billing_by_type.map((item) => item.sop_amount_cr),
              backgroundColor: '#3a7de0',
              stack: 'billing',
              borderRadius: 6,
            },
            {
              label: 'OA',
              data: overview.billing_by_type.map((item) => item.oa_amount_cr),
              backgroundColor: '#3698d5',
              stack: 'billing',
              borderRadius: 6,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          plugins: { legend: { position: 'bottom' } },
          scales: {
            x: { stacked: true, grid: { display: false } },
            y: { stacked: true, beginAtZero: true },
          },
        },
      }),
    );
  }

  private renderPaymentTrendChart(): void {
    const canvas = this.paymentTrendCanvas?.nativeElement;
    const overview = this.overview();
    if (!canvas || !overview) return;

    this.charts.push(
      new Chart(canvas, {
        type: 'line',
        data: {
          labels: overview.outstanding_paid_trend.map((item) => item.period),
          datasets: [
            {
              label: 'Outstanding',
              data: overview.outstanding_paid_trend.map((item) => item.outstanding_amount_cr),
              borderColor: '#f05a57',
              backgroundColor: '#f05a57',
              tension: 0.35,
            },
            {
              label: 'Paid Amount',
              data: overview.outstanding_paid_trend.map((item) => item.paid_amount_cr),
              borderColor: '#1faf63',
              backgroundColor: '#1faf63',
              tension: 0.35,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          plugins: { legend: { position: 'bottom' } },
          scales: {
            x: { grid: { display: false } },
            y: { beginAtZero: true },
          },
        },
      }),
    );
  }

  private destroyCharts(): void {
    this.charts.forEach((chart) => chart.destroy());
    this.charts = [];
  }
}
